import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-input-mask';
import FormGroup from '../../../FormGroup';
import Helper from './Helper';
import config from 'config.js';
import { I18nContext } from 'utils/I18n/context';

const PaymentSystem = (props) => {
    const { t } = useContext(I18nContext);
    const [errors, setErrors] = useState(props.errors || {});

    useEffect(() => {
        setErrors(props.errors || {});
    }, [props.errors]);

    const staticNames = t?.paymentPage?.settingModal || {};

    return (
        <Helper>
            {({ renderTitle }) => (
                <React.Fragment>
                    {renderTitle()}
                    <FullName
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <EdrpouIpn
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <BankName
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <BankAccount
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <Bic
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <PurposeOfPayment
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <Iban
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <CryptoCurrency
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <CryptoNetwork
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <CardNumber
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <Description
                        state={{ errors }}
                        props={props}
                        staticNames={staticNames}
                    />
                    <br/>
                </React.Fragment>
            )}
        </Helper>
    );
};

PaymentSystem.propTypes = {
    errors: PropTypes.object,
    paymentSystem: PropTypes.object,
    fullName: PropTypes.string,
    onChangeFullName: PropTypes.func,
    edrpouIpn: PropTypes.string,
    onChangeEdrpouIpn: PropTypes.func,
    bankName: PropTypes.string,
    onChangeBankName: PropTypes.func,
    bankAccount: PropTypes.string,
    onChangeBankAccount: PropTypes.func,
    bic: PropTypes.string,
    onChangeBic: PropTypes.func,
    purposeOfPayment: PropTypes.string,
    onChangePurposeOfPayment: PropTypes.func,
    iban: PropTypes.string,
    onChangeIban: PropTypes.func,
    cryptoCurrency: PropTypes.string,
    onChangeCryptoCurrency: PropTypes.func,
    cryptoNetwork: PropTypes.string,
    onChangeCryptoNetwork: PropTypes.func,
    cardNumber: PropTypes.string,
    onChangeCardNumber: PropTypes.func,
    description: PropTypes.string,
    onChangeDescription: PropTypes.func
};

PaymentSystem.defaultProps = {
    errors: {},
    paymentSystem: { reqFields: [], notReqFields: [] },
    fullName: '',
    onChangeFullName: () => {},
    edrpouIpn: '',
    onChangeEdrpouIpn: () => {},
    bankName: '',
    onChangeBankName: () => {},
    bankAccount: '',
    onChangeBankAccount: () => {},
    bic: '',
    onChangeBic: () => {},
    purposeOfPayment: '',
    onChangePurposeOfPayment: () => {},
    iban: '',
    onChangeIban: () => {},
    cryptoCurrency: '',
    onChangeCryptoCurrency: () => {},
    cryptoNetwork: '',
    onChangeCryptoNetwork: () => {},
    cardNumber: '',
    onChangeCardNumber: () => {},
    description: '',
    onChangeDescription: () => {}
};

export default PaymentSystem;

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function FullName({ state, props, staticNames }) {
    let showRequired = false;
    if (props.paymentSystem?.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('full_name' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem?.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('full_name' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.fullName}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.fullName}
        >
            <input
                className="form-control"
                onChange={props.onChangeFullName}
                type="text"
                value={props.fullName}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function EdrpouIpn({ state, props, staticNames }) {
    let showRequired = false;

    if (!props.paymentSystem) {
        return null;
    }

    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('edrpou_ipn' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('edrpou_ipn' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.edrpouIpn}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.edrpouIpn}
        >
            <input
                className="form-control"
                onChange={props.onChangeEdrpouIpn}
                type="text"
                value={props.edrpouIpn}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function BankName({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('bank_name' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('bank_name' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.bankName}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.bankName}
        >
            <input
                className="form-control"
                onChange={props.onChangeBankName}
                type="text"
                value={props.bankName}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function BankAccount({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('bank_account' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('bank_account' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.bankAccount}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.bankAccount}
        >
            <input
                className="form-control"
                onChange={props.onChangeBankAccount}
                type="text"
                value={props.bankAccount}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function Bic({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('bic' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('bic' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.bic}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.bic}
        >
            <input
                className="form-control"
                onChange={props.onChangeBic}
                type="text"
                value={props.bic}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function PurposeOfPayment({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('purpose_of_payment' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('purpose_of_payment' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.purposeOfPayment}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.purposeOfPayment}
        >
            <input
                className="form-control"
                onChange={props.onChangePurposeOfPayment}
                type="text"
                value={props.purposeOfPayment}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function Iban({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('iban' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('iban' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.iban}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.iban}
        >
            <input
                className="form-control"
                onChange={props.onChangeIban}
                type="text"
                value={props.iban}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function CryptoCurrency({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('crypto_currency' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('crypto_currency' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.cryptoCurrency}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.cryptoCurrency}
        >
            <input
                className="form-control"
                onChange={props.onChangeCryptoCurrency}
                type="text"
                value={props.cryptoCurrency}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function CryptoNetwork({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('crypto_network' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('crypto_network' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.cryptoNetwork}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.cryptoNetwork}
        >
            <input
                className="form-control"
                onChange={props.onChangeCryptoNetwork}
                type="text"
                value={props.cryptoNetwork}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function CardNumber({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('card_number' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('card_number' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.cardNumber}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.cardNumber}
        >
            <input
                className="form-control"
                value={props.cardNumber}
                type="text"
                onChange={props.onChangeCardNumber}
                required={showRequired}
            />
        </FormGroup>
    );
}

/**
 * @param {object} state
 * @param {object} props
 * @param {object} staticNames
 * @returns {JSX.Element}
 */
function Description({ state, props, staticNames }) {
    if (!props.paymentSystem) {
        return null;
    }

    let showRequired = false;
    if (props.paymentSystem.reqFields?.length) {
        props.paymentSystem.reqFields.forEach(function(field) {
            if ('description' === field) {
                showRequired = true;
            }
        });
    }

    let showNotRequired = false;
    if (props.paymentSystem.notReqFields?.length) {
        props.paymentSystem.notReqFields.forEach(function(field) {
            if ('description' === field) {
                showNotRequired = true;
            }
        });
    }

    if (!showRequired && !showNotRequired) {
        return null;
    }

    return (
        <FormGroup
            label={`${staticNames.description}`}
            labelClass={`${showRequired ? "required" : ""}`}
            errors={state.errors.description}
        >
            <textarea
                className="form-control"
                rows={3}
                onChange={props.onChangeDescription}
                value={props.description}
                required={showRequired}
            />
        </FormGroup>
    );
}